import * as React from 'react';
import { useEffect, useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { DialogRoute } from 'bernie-core';
import { Layer, Viewport, ViewMedium, ViewSmall } from 'bernie-view';

import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkNotificationBadge1 } from 'uitk-react-badge';
import { UitkLink } from 'uitk-react-link';
import { UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';

import analyticsWrapper from 'utility/analytics-wrapper';
import { useStores } from 'utility/hooks';
import { trackAnalytics, getSearchFormTriggerDateLabel } from 'utility';
import { ButtonTracked, TertiaryButtonTracked } from 'components/shared/tracked';
import { SortFilterDialog } from 'components/search/sort-filter';
import { TravelerCountInfo, TripTabs } from '.';
import { FlightsSearchFormTravelersOption } from 'types/search/wizard';
import { withLocalization } from '@shared-ui/localization-context';
import { LocalizedInteractableFieldComponentProps } from 'src/types/shared';

interface PlaybackProps extends LocalizedInteractableFieldComponentProps {
  searchFormHeadingRef: React.MutableRefObject<HTMLHeadingElement>;
}

let shouldSaveChanges = false;
let shouldFocusOnTrigger = false;

export const Playback = withLocalization(
  observer(({ searchFormHeadingRef, onFieldInteraction, l10n }: PlaybackProps) => {
    const { routeStore, searchCriteriaStore, searchFormStore, sortFilterStore, context, analytics } = useStores();
    const searchFormTriggerRef = useRef<HTMLInputElement>(null);
    const [searchFormExpanded, setSearchFormExpanded] = useState(false);
    const [shouldOpenSearchFormOnError, setShouldOpenSearchFormOnError] = useState(true);
    const [shouldCloseSearchFormOnSearchLoading, setShouldCloseSearchFormOnSearchLoading] = useState(false);
    const { searchForm, essEndpoint } = searchFormStore;
    const { expandFormAnalytics, collapseFormAnalytics, closeActionAccessibility, closeFormAction } = searchForm;
    const sortFilterDialogId = 'sort-filter-dialog';

    const openSearchForm = useCallback(() => {
      setSearchFormExpanded(true);
      trackAnalytics(expandFormAnalytics, analytics);
      onFieldInteraction && onFieldInteraction();
      shouldSaveChanges = false;
      if (!searchFormStore.loading) {
        setShouldCloseSearchFormOnSearchLoading(true);
      }
    }, [analytics, expandFormAnalytics, onFieldInteraction, searchFormStore.loading]);

    const closeSearchForm = useCallback(() => {
      setSearchFormExpanded(false);
      setShouldOpenSearchFormOnError(true);
      setShouldCloseSearchFormOnSearchLoading(false);

      if (shouldSaveChanges) {
        searchCriteriaStore.syncWithSearchFormStore(searchFormStore);
        searchCriteriaStore.clearSortFilterSearchCriteria();
        searchCriteriaStore.triggerSearch(routeStore, context);
        sortFilterStore.loading = true;
        analytics.resetAnalyticsForNewPage();
      } else {
        if (searchFormStore.loading) {
          searchFormStore.syncWithSearchCriteriaStore(searchCriteriaStore);
        } else {
          searchFormStore.restoreSearchFormToInitialState();
        }
        trackAnalytics(collapseFormAnalytics, analytics);
      }
    }, [
      collapseFormAnalytics,
      context,
      routeStore,
      searchCriteriaStore,
      sortFilterStore.loading,
      searchFormStore,
      analytics
    ]);

    const verifyTravelersErrors = useCallback(() => {
      const travelerOption = searchForm.searchOptions.find(
        option => option.__typename === 'FlightsSearchFormTravelersOption'
      ) as FlightsSearchFormTravelersOption;

      if (travelerOption.currentErrors && travelerOption.currentErrors.length > 0) {
        const travelersDialogQuery = routeStore.mergeQueryString(routeStore.search, {
          pwaDialog: 'FlightsSearchFormTravelersOption-dialog'
        });
        routeStore.updateSearch(travelersDialogQuery);
      }
    }, [routeStore, searchForm.searchOptions]);

    const submitChanges = useCallback(() => {
      const valid = searchFormStore.validate(analytics);

      searchFormStore.clearErrorRef();

      if (!valid) {
        verifyTravelersErrors();

        return;
      }

      shouldSaveChanges = true;
      sortFilterStore.clearFilters();
      analyticsWrapper.resetAnalyticsPayLoad(analytics);
      closeSearchForm();
    }, [analytics, closeSearchForm, searchFormStore, sortFilterStore, verifyTravelersErrors]);

    const sortFilterDialogQuery = routeStore.mergeQueryString(routeStore.search, {
      pwaDialog: sortFilterDialogId
    });

    const onSortFilterDialogDismiss = useCallback(() => {
      searchFormStore.restoreSearchFormToInitialState();
      routeStore.closeDialog('pwaDialog');
    }, [routeStore, searchFormStore]);

    const openSortAndFilterDialog = useCallback(() => {
      searchFormStore.restoreSearchFormToInitialState();
      routeStore.updateSearch(sortFilterDialogQuery);
    }, [routeStore, searchFormStore, sortFilterDialogQuery]);

    const renderSortFilterTrigger = () => {
      const revealActionAnalytics = sortFilterStore && sortFilterStore.revealActionAnalytics;

      const revealAction = sortFilterStore && sortFilterStore.revealAction;
      const appliedFilterCount = (revealAction && revealAction.appliedFilterCount) || 0;
      const showNumberOfActiveFilters = appliedFilterCount > 0;
      const shouldEnableSortFilterButton = revealAction && revealAction.enabled;
      const iconAndLabel = revealAction && revealAction.iconAndLabel;
      const iconId = (iconAndLabel && iconAndLabel.icon && iconAndLabel.icon.id) || 'tune';
      const label = iconAndLabel && iconAndLabel.label;

      return (
        <div data-test-id={`${sortFilterDialogId}-trigger`}>
          <UitkLink>
            <ButtonTracked
              onClick={openSortAndFilterDialog}
              disabled={!shouldEnableSortFilterButton}
              {...revealActionAnalytics}
            >
              <UitkLayoutFlex space="two" alignItems="center">
                <>
                  <UitkLayoutFlexItem>
                    {showNumberOfActiveFilters ? (
                      <UitkNotificationBadge1 text={appliedFilterCount.toString()} />
                    ) : (
                      <UitkIcon name={iconId} size={UitkIconSize.SMALL} />
                    )}
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem>
                    <UitkText size={400} inline={true} weight="medium">
                      {label}
                    </UitkText>
                  </UitkLayoutFlexItem>
                </>
              </UitkLayoutFlex>
            </ButtonTracked>
          </UitkLink>
          <DialogRoute id={sortFilterDialogId}>
            <Layer id={`${sortFilterDialogId}-layer`}>
              <SortFilterDialog
                onOpen={onFieldInteraction}
                onDismiss={onSortFilterDialogDismiss}
                sortAndFilterResult={sortFilterStore}
                dialogId={sortFilterDialogId}
              />
            </Layer>
          </DialogRoute>
        </div>
      );
    };

    const renderSearchFormTrigger = () => {
      const { searchFormInitialState } = searchFormStore;
      const departureAndReturnDates = searchForm && searchForm.departureAndReturnDates;
      const { currentOriginAndDestinationLocationsLabel } = searchFormStore;

      const departureAndReturnDatesLabels = getSearchFormTriggerDateLabel(
        departureAndReturnDates,
        searchForm.journeys[0],
        searchFormInitialState,
        searchFormStore.loading,
        l10n
      );

      const travelerTypeOptions =
        searchForm &&
        searchForm.searchOptions &&
        (searchForm.searchOptions.find(
          searchOption => searchOption.__typename === 'FlightsSearchFormTravelersOption'
        ) as FlightsSearchFormTravelersOption);

      return (
        <div data-test-id="search-dialog-trigger">
          <UitkLink inline={true} onClick={openSearchForm} className="uitk-type-left">
            <button ref={searchFormTriggerRef}>
              <Viewport>
                <ViewSmall>
                  <UitkLayoutGrid columns={1}>
                    <UitkLayoutGridItem>
                      <UitkText size={500} weight="bold" overflow="truncate">
                        {currentOriginAndDestinationLocationsLabel}
                      </UitkText>
                    </UitkLayoutGridItem>
                    <UitkLayoutGridItem>
                      <UitkText size={300} overflow="truncate">
                        {departureAndReturnDatesLabels.shortMessage}
                      </UitkText>
                    </UitkLayoutGridItem>
                  </UitkLayoutGrid>
                </ViewSmall>
                <ViewMedium>
                  <UitkLayoutGrid columns={1}>
                    <UitkLayoutGridItem>
                      <UitkText size={500} weight="bold" overflow="truncate">
                        {currentOriginAndDestinationLocationsLabel}
                      </UitkText>
                    </UitkLayoutGridItem>
                    <UitkLayoutGridItem>
                      <UitkText size={300} overflow="truncate">
                        {departureAndReturnDatesLabels.longMessage}
                        <TravelerCountInfo travelerTypeOptions={travelerTypeOptions} />
                      </UitkText>
                    </UitkLayoutGridItem>
                  </UitkLayoutGrid>
                </ViewMedium>
              </Viewport>
            </button>
          </UitkLink>
        </div>
      );
    };

    const validateSearchFormLocation = useCallback(() => {
      let valid = true;

      searchForm.journeys.forEach(journey => {
        const { destinationLocation, originLocation } = journey;
        valid = valid && !destinationLocation.currentError && !originLocation.currentError;
      });

      return valid;
    }, [searchForm.journeys]);

    useEffect(() => {
      if (shouldCloseSearchFormOnSearchLoading && searchFormStore.loading) {
        closeSearchForm();
      } else if (!searchFormExpanded && shouldOpenSearchFormOnError && !validateSearchFormLocation()) {
        openSearchForm();
        setShouldOpenSearchFormOnError(false);
      }
    }, [
      closeSearchForm,
      openSearchForm,
      searchFormExpanded,
      shouldCloseSearchFormOnSearchLoading,
      shouldOpenSearchFormOnError,
      validateSearchFormLocation,
      setShouldOpenSearchFormOnError,
      searchFormStore.loading
    ]);

    useEffect(() => {
      if (searchFormExpanded && searchFormHeadingRef.current) {
        searchFormHeadingRef.current.focus();
        shouldFocusOnTrigger = true;
      } else if (shouldFocusOnTrigger && !searchFormExpanded && searchFormTriggerRef.current) {
        searchFormTriggerRef.current.focus();
      }
    }, [searchFormExpanded, searchFormHeadingRef, searchFormStore.loading]);

    return (
      <>
        {searchFormExpanded ? (
          <div data-test-id="playback">
            <TripTabs essEndpoint={essEndpoint} onFieldInteraction={onFieldInteraction} />
            <UitkLayoutGrid columns={{ medium: 6, small: 1 }}>
              <UitkLayoutGridItem colSpan={{ medium: 2, small: 1 }}>
                <UitkPrimaryButton
                  onClick={submitChanges}
                  isFullWidth={true}
                  size={UitkButtonSize.LARGE}
                  text={searchForm.searchAction}
                />
              </UitkLayoutGridItem>
            </UitkLayoutGrid>
            <TertiaryButtonTracked
              onClick={closeSearchForm}
              isFullWidth={true}
              size={UitkButtonSize.LARGE}
              text={closeFormAction && closeFormAction.displayAction}
              aria-label={closeActionAccessibility}
              {...(closeFormAction && closeFormAction.analytics)}
            />
          </div>
        ) : (
          <UitkLayoutFlex space="three" data-test-id="playback">
            <UitkLayoutFlexItem grow={1}>{renderSearchFormTrigger()}</UitkLayoutFlexItem>
            <UitkLayoutFlexItem grow={0}>{renderSortFilterTrigger()}</UitkLayoutFlexItem>
          </UitkLayoutFlex>
        )}
      </>
    );
  })
) as React.ComponentClass<PlaybackProps>;
